import React, { useState, useRef, useEffect, forwardRef } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import useControllableState from '../hooks/useControllableState'
import useMergedRef from '../hooks/useMergeRef'
import capitalize from '../utils/capitalize'
import RangeCalendar from './RangeCalendar'
import BasePicker from './BasePicker'
import { useConfig } from '../ConfigProvider'

dayjs.extend(customParseFormat)

const validationRule = (val) =>
    Array.isArray(val) &&
    val.length === 2 &&
    val.every((v) => v instanceof Date)

const isFirstDateSet = (val) =>
    Array.isArray(val) && val.length === 2 && val[0] instanceof Date

const NumericDatePickerRange = forwardRef((props, ref) => {
    const {
        className,
        clearable,
        clearButton,
        closePickerOnChange,
        dateViewCount,
        dayClassName,
        dayStyle,
        defaultMonth,
        defaultOpen,
        defaultValue,
        defaultView,
        disabled,
        disableDate,
        enableHeaderLabel,
        disableOutOfMonth,
        firstDayOfWeek,
        hideOutOfMonthDates,
        hideWeekdays,
        inputFormat,
        inputPrefix,
        inputSuffix,
        labelFormat,
        seperator,
        locale,
        maxDate,
        minDate,
        onChange,
        onDropdownClose,
        onDropdownOpen,
        openPickerOnClear,
        renderDay,
        singleDate,
        size,
        style,
        value,
        weekendDays,
        yearLabelFormat,
        ...rest
    } = props

    const { locale: themeLocale } = useConfig()

    const finalLocale = locale || themeLocale

    const dateFormat = inputFormat || 'DD/MM/YYYY'

    const [dropdownOpened, setDropdownOpened] = useState(defaultOpen)

    const inputRef = useRef()

    const [_value, setValue] = useControllableState({
        prop: value,
        defaultProp: defaultValue !== undefined ? defaultValue : [null, null],
        onChange,
    })

    const valueValid = validationRule(_value)
    const firstValueValid = isFirstDateSet(_value)

    const [inputText, setInputText] = useState('')
    const lastValue = useRef('')

    const [focused, setFocused] = useState(false)

    // Update input text when _value changes (e.g. from calendar click or external prop change)
    useEffect(() => {
        if (valueValid && !focused) {
            const firstDateLabel = _value[0]
                ? capitalize(dayjs(_value[0]).locale(finalLocale).format(dateFormat))
                : ''
            const secondDateLabel = _value[1]
                ? capitalize(dayjs(_value[1]).locale(finalLocale).format(dateFormat))
                : ''
            const formatted = `${firstDateLabel}${seperator}${secondDateLabel}`
            if (formatted !== lastValue.current) {
                setInputText(formatted)
                lastValue.current = formatted
            }
        } else if (!_value[0] && !_value[1] && !focused) {
            setInputText('')
            lastValue.current = ''
        }
    }, [_value, dateFormat, finalLocale, seperator, valueValid, focused])

    const formatInput = (rawVal, sep) => {
        const clean = rawVal.replace(/\D/g, '').slice(0, 16)
        let result = ''
        
        // First date
        if (clean.length > 0) {
            result += clean.slice(0, 2)
            if (clean.length > 2) {
                result += '/' + clean.slice(2, 4)
                if (clean.length > 4) {
                    result += '/' + clean.slice(4, 8)
                }
            }
        }
        
        // Second date
        if (clean.length > 8) {
            result += sep
            const secondPart = clean.slice(8)
            result += secondPart.slice(0, 2)
            if (secondPart.length > 2) {
                result += '/' + secondPart.slice(2, 4)
                if (secondPart.length > 4) {
                    result += '/' + secondPart.slice(4, 8)
                }
            }
        }
        return result
    }

    const isDateWithinRange = (dateStr) => {
        if (!dateStr) return false
        const parts = dateStr.split(/[^0-9]/)
        if (parts.length === 3) {
            const day = parseInt(parts[0], 10)
            const month = parseInt(parts[1], 10)
            const year = parseInt(parts[2], 10)
            
            if (day < 1 || day > 31) return false
            if (month < 1 || month > 12) return false
            if (year < 1900 || year > 2072) return false
            return true
        }
        return false
    }

    const isValidRange = (startStr, endStr) => {
        if (!isDateWithinRange(startStr) || !isDateWithinRange(endStr)) return false
        
        const startDate = dayjs(startStr, dateFormat, finalLocale)
        const endDate = dayjs(endStr, dateFormat, finalLocale)
        
        if (!startDate.isValid() || !endDate.isValid()) return false
        
        return startDate.isBefore(endDate) || startDate.isSame(endDate)
    }

    const parseAndSetDates = (formattedStr) => {
        const parts = formattedStr.split(seperator)
        if (parts.length === 2) {
            const startStr = parts[0]
            const endStr = parts[1]
            
            if (startStr.length === 10 && endStr.length === 10) {
                if (isValidRange(startStr, endStr)) {
                    const startDate = dayjs(startStr, dateFormat, true)
                    const endDate = dayjs(endStr, dateFormat, true)
                    
                    if (startDate.isValid() && endDate.isValid()) {
                        setValue([startDate.toDate(), endDate.toDate()])
                    }
                }
            }
        } else if (parts.length === 1) {
            const startStr = parts[0]
            if (startStr.length === 10) {
                if (isDateWithinRange(startStr)) {
                    const startDate = dayjs(startStr, dateFormat, true)
                    if (startDate.isValid()) {
                        setValue([startDate.toDate(), null])
                    }
                }
            }
        }
    }

    const setDatesFromInput = () => {
        const parts = inputText.split(seperator)
        if (parts.length === 2) {
            const startStr = parts[0]
            const endStr = parts[1]
            if (startStr.length === 10 && endStr.length === 10) {
                if (isValidRange(startStr, endStr)) {
                    const startDate = dayjs(startStr, dateFormat, true).toDate()
                    const endDate = dayjs(endStr, dateFormat, true).toDate()
                    setValue([startDate, endDate])
                    lastValue.current = inputText
                    return
                }
            }
        } else if (parts.length === 1) {
            const startStr = parts[0]
            if (startStr.length === 10) {
                if (isDateWithinRange(startStr)) {
                    const startDate = dayjs(startStr, dateFormat, true).toDate()
                    setValue([startDate, null])
                    lastValue.current = inputText
                    return
                }
            }
        }

        if (valueValid) {
            const firstDateLabel = _value[0]
                ? capitalize(dayjs(_value[0]).locale(finalLocale).format(dateFormat))
                : ''
            const secondDateLabel = _value[1]
                ? capitalize(dayjs(_value[1]).locale(finalLocale).format(dateFormat))
                : ''
            const formatted = `${firstDateLabel}${seperator}${secondDateLabel}`
            setInputText(formatted)
            lastValue.current = formatted
        } else {
            setValue([null, null])
            setInputText('')
            lastValue.current = ''
        }
    }

    const handleInputBlur = (event) => {
        let isValid = true
        if (inputText) {
            const parts = inputText.split(seperator)
            if (parts.length === 2) {
                const startStr = parts[0]
                const endStr = parts[1]
                if (startStr.length !== 10 || endStr.length !== 10 || !isValidRange(startStr, endStr)) {
                    isValid = false
                }
            } else if (parts.length === 1) {
                const startStr = parts[0]
                if (startStr.length !== 10 || !isDateWithinRange(startStr)) {
                    isValid = false
                }
            } else {
                isValid = false
            }
        }
        typeof rest.onBlur === 'function' && rest.onBlur(event, isValid)
        setFocused(false)
        setDatesFromInput()
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            setDropdownOpened(false)
            setDatesFromInput()
        }
    }

    const handleInputFocus = (event) => {
        typeof rest.onFocus === 'function' && rest.onFocus(event)
        setFocused(true)
    }

    const handleInputChange = (e) => {
        let val = e.target.value
        
        if (val.length < lastValue.current.length) {
            const lastCharDeleted = lastValue.current.slice(val.length)
            if (lastCharDeleted.match(/[^0-9]/)) {
                const temp = val.replace(/[^0-9]+$/, '')
                val = temp.slice(0, -1)
            }
        }
        
        const formatted = formatInput(val, seperator)
        setInputText(formatted)
        lastValue.current = formatted
        
        parseAndSetDates(formatted)
    }

    const handleValueChange = (range) => {
        setValue(range)
        if (closePickerOnChange && validationRule(range)) {
            setDropdownOpened(false)
            onDropdownClose?.()
            window.setTimeout(() => inputRef.current?.focus(), 0)
        }
    }

    const handleClear = () => {
        setValue([null, null])
        setInputText('')
        lastValue.current = ''
        setDropdownOpened(true)
        openPickerOnClear && onDropdownOpen?.()
        inputRef.current?.focus()
    }

    const handleDropdownToggle = (isOpened) => {
        if (!isOpened && firstValueValid && _value[1] === null) {
            _value[1] = _value[0]
            setValue([_value[0], _value[0]])
        }

        if (!_value[0] && !_value[1] && defaultValue) {
            setValue(defaultValue)
        }

        setDropdownOpened(isOpened)
    }

    return (
        <BasePicker
            dropdownOpened={dropdownOpened}
            setDropdownOpened={handleDropdownToggle}
            ref={useMergedRef(ref, inputRef)}
            size={size}
            style={style}
            className={className}
            inputLabel={inputText}
            clearable={clearable && firstValueValid}
            clearButton={clearButton}
            onClear={handleClear}
            dateViewCount={dateViewCount}
            onDropdownClose={onDropdownClose}
            onDropdownOpen={onDropdownOpen}
            disabled={disabled}
            inputPrefix={inputPrefix}
            inputSuffix={inputSuffix}
            inputtable={true}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            {...rest}
        >
            <RangeCalendar
                locale={finalLocale}
                defaultMonth={valueValid ? _value[0] : defaultMonth}
                value={_value}
                onChange={handleValueChange}
                labelFormat={labelFormat}
                dayClassName={dayClassName}
                dayStyle={dayStyle}
                disableOutOfMonth={disableOutOfMonth}
                minDate={minDate}
                maxDate={maxDate}
                disableDate={disableDate}
                firstDayOfWeek={firstDayOfWeek}
                enableHeaderLabel={enableHeaderLabel}
                singleDate={singleDate}
                dateViewCount={dateViewCount}
                defaultView={defaultView}
                hideOutOfMonthDates={hideOutOfMonthDates}
                hideWeekdays={hideWeekdays}
                renderDay={renderDay}
                weekendDays={weekendDays}
                yearLabelFormat={yearLabelFormat}
            />
        </BasePicker>
    )
})

NumericDatePickerRange.defaultProps = {
    closePickerOnChange: true,
    labelFormat: {
        month: 'MMM',
        year: 'YYYY',
    },
    defaultOpen: false,
    seperator: ' -> ',
    clearable: true,
    firstDayOfWeek: 'monday',
    singleDate: false,
    dateViewCount: 1,
    openPickerOnClear: false,
}

export default NumericDatePickerRange
